/**
 * PajemploiOnboardingGuide — Multi-step in-app onboarding for parents
 * explaining the Pajemploi tierce-déclaration flow and collecting
 * explicit mandate consent before they can declare.
 *
 * Shown once, the first time a parent clicks "Déclarer Pajemploi".
 * Once consent is recorded (backend), this dialog is never shown again.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  ShieldCheck,
  FileText,
  Users,
  Calculator,
  Send,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Info,
  Loader2,
  CalendarClock,
} from 'lucide-react';
import { WizardStepper } from '../WizardStepper';

const STEPS = [
  { key: 'intro', label: 'Présentation' },
  { key: 'flow', label: 'Fonctionnement' },
  { key: 'consent', label: 'Consentement' },
];

export const PajemploiOnboardingGuide = ({
  open,
  onOpenChange,
  onConsent,
  submitting = false,
}) => {
  const [step, setStep] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);

  const handleClose = () => {
    setStep(0);
    setConsentChecked(false);
    onOpenChange(false);
  };

  const handleConsent = () => {
    if (consentChecked && onConsent) {
      onConsent();
    }
  };

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-xl bg-[var(--bg-primary)] p-0 flex flex-col max-h-[90vh]"
        data-testid="pajemploi-onboarding-guide"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-3">
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2 text-lg"
              style={{ color: 'var(--primary-700)', fontFamily: 'var(--font-secondary)' }}
            >
              <ShieldCheck className="h-5 w-5 text-[var(--primary-400)]" />
              Guide Pajemploi — Tierce Déclaration
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {STEPS[step].label} — Étape {step + 1} / {STEPS.length}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <WizardStepper
            totalSteps={STEPS.length}
            currentStep={step + 1}
            labels={STEPS.map(s => s.label)}
          />
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {/* ─── STEP 1: Présentation ─── */}
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-intro">
              <div
                className="p-4 rounded-xl border"
                style={{
                  backgroundColor: 'var(--primary-50)',
                  borderColor: 'var(--primary-200)',
                }}
              >
                <h3
                  className="text-base font-semibold mb-2 flex items-center gap-2"
                  style={{ color: 'var(--primary-700)' }}
                >
                  <FileText className="h-4 w-4" />
                  Qu'est-ce que la Tierce Déclaration ?
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  La <strong>Tierce Déclaration Pajemploi</strong> permet à Nannyou de déclarer
                  la rémunération de votre assistante maternelle <strong>directement auprès de l'URSSAF / Pajemploi</strong>,
                  en votre nom. Cela vous évite de devoir vous connecter manuellement sur le site Pajemploi chaque mois.
                </p>
              </div>

              <div className="grid gap-3">
                <InfoCard
                  icon={Calculator}
                  title="Estimation automatique"
                  description="Nannyou calcule les cotisations sociales (salariales et patronales) en utilisant les taux officiels URSSAF, puis les vérifie via l'API Pajemploi."
                />
                <InfoCard
                  icon={Send}
                  title="Déclaration officielle"
                  description="La déclaration est soumise directement à Pajemploi avec une pré-validation préalable. Vous recevez un numéro de référence officiel."
                />
                <InfoCard
                  icon={CalendarClock}
                  title="Règle du 25 du mois"
                  description="Les déclarations pour le mois en cours ne peuvent être soumises qu'à partir du 25 du mois. Avant cette date, seuls les mois passés sont déclarables."
                />
              </div>
            </div>
          )}

          {/* ─── STEP 2: Fonctionnement ─── */}
          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-flow">
              <h3
                className="text-base font-semibold flex items-center gap-2"
                style={{ color: 'var(--primary-700)' }}
              >
                <Users className="h-4 w-4" />
                Comment ça fonctionne ?
              </h3>

              <div className="space-y-3">
                <FlowStep
                  number={1}
                  title="Mandat de Tierce Déclaration"
                  description="Nannyou enregistre automatiquement un mandat auprès de Pajemploi pour agir en votre nom. Ce mandat est requis par l'URSSAF."
                  highlight
                />
                <FlowStep
                  number={2}
                  title="Association Employeur / Salariée"
                  description="Votre assistante maternelle est automatiquement associée à votre compte employeur via l'API officielle (PAJE023). Cela lie votre numéro Pajemploi au NIR de la nounou."
                />
                <FlowStep
                  number={3}
                  title="Estimation des cotisations"
                  description="Nannyou interroge l'API Pajemploi pour obtenir une estimation officielle des cotisations du mois. Vous vérifiez les montants avant de continuer."
                />
                <FlowStep
                  number={4}
                  title="Pré-déclaration & Vérification"
                  description="Une pré-déclaration est envoyée pour vérification. Si elle est validée, vous confirmez l'envoi final."
                />
                <FlowStep
                  number={5}
                  title="Déclaration officielle"
                  description="La déclaration finale est soumise. Vous recevez un numéro de référence Pajemploi. La déclaration apparaît dans votre historique."
                />
              </div>

              <div
                className="flex items-start gap-2 p-3 rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--info-light)',
                  color: 'var(--info-dark)',
                }}
              >
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  En cas d'erreur (mandat absent, salarié non associé, etc.),
                  Nannyou corrige automatiquement le problème et réessaie.
                  Vous pouvez toujours enregistrer la déclaration <strong>localement</strong> en
                  attendant.
                </span>
              </div>
            </div>
          )}

          {/* ─── STEP 3: Consentement ─── */}
          {step === 2 && (
            <div className="space-y-5" data-testid="onboarding-step-consent">
              <div
                className="p-4 rounded-xl border"
                style={{
                  backgroundColor: 'var(--secondary-50)',
                  borderColor: 'var(--secondary-200)',
                }}
              >
                <h3
                  className="text-base font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--secondary-700)' }}
                >
                  <ShieldCheck className="h-4 w-4" />
                  Consentement requis
                </h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
                  En acceptant, vous autorisez Nannyou à :
                </p>
                <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <ConsentItem>
                    Enregistrer un <strong>mandat de tierce déclaration</strong> auprès de Pajemploi / URSSAF en votre nom
                  </ConsentItem>
                  <ConsentItem>
                    <strong>Associer votre assistante maternelle</strong> (salariée) à votre compte employeur via l'API officielle
                  </ConsentItem>
                  <ConsentItem>
                    <strong>Pré-déclarer et déclarer</strong> la rémunération mensuelle de votre assistante maternelle auprès de Pajemploi
                  </ConsentItem>
                  <ConsentItem>
                    Transmettre les données nécessaires (numéro Pajemploi, NIR, salaires, heures) à l'API URSSAF dans le cadre strict de la déclaration
                  </ConsentItem>
                </ul>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <Checkbox
                  id="pajemploi-consent"
                  checked={consentChecked}
                  onCheckedChange={(checked) => setConsentChecked(!!checked)}
                  data-testid="pajemploi-consent-checkbox"
                  className="mt-1"
                  style={{
                    borderColor: consentChecked ? 'var(--primary-400)' : 'var(--neutral-400)',
                    backgroundColor: consentChecked ? 'var(--primary-400)' : 'transparent',
                  }}
                />
                <label
                  htmlFor="pajemploi-consent"
                  className="text-sm leading-relaxed cursor-pointer select-none"
                  style={{ color: 'var(--text-primary)' }}
                >
                  J'ai lu et compris le fonctionnement de la Tierce Déclaration Pajemploi.
                  J'autorise Nannyou à agir en mon nom pour enregistrer le mandat,
                  associer ma salariée, et déclarer la rémunération auprès de Pajemploi / URSSAF.
                </label>
              </div>
            </div>
          )}
        </div>

        {/* ─── Footer ─── */}
        <div
          className="border-t px-6 py-4 flex justify-between items-center"
          style={{
            borderColor: 'var(--neutral-200)',
            backgroundColor: 'var(--bg-primary)',
          }}
        >
          {step > 0 ? (
            <Button
              variant="outline"
              onClick={goPrev}
              className="gap-1"
              data-testid="onboarding-prev-btn"
            >
              <ChevronLeft className="h-4 w-4" /> Retour
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={handleClose}
              data-testid="onboarding-close-btn"
              style={{ color: 'var(--text-secondary)' }}
            >
              Fermer
            </Button>
          )}

          {step < STEPS.length - 1 ? (
            <Button
              onClick={goNext}
              className="gap-1"
              style={{ backgroundColor: 'var(--primary-400)', color: 'white' }}
              data-testid="onboarding-next-btn"
            >
              Suivant <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleConsent}
              disabled={!consentChecked || submitting}
              className="gap-2"
              style={{
                backgroundColor: consentChecked ? 'var(--success)' : 'var(--neutral-300)',
                color: 'white',
              }}
              data-testid="onboarding-consent-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Enregistrement...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" /> J'accepte et je continue
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Sub-components ─── */

const InfoCard = ({ icon: Icon, title, description }) => (
  <div
    className="flex items-start gap-3 p-3 rounded-lg border transition-colors duration-150"
    style={{
      borderColor: 'var(--neutral-200)',
      backgroundColor: 'var(--neutral-50)',
    }}
  >
    <div
      className="p-2 rounded-lg shrink-0"
      style={{ backgroundColor: 'var(--primary-100)' }}
    >
      <Icon className="h-4 w-4 text-[var(--primary-500)]" />
    </div>
    <div>
      <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
        {title}
      </h4>
      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {description}
      </p>
    </div>
  </div>
);

const FlowStep = ({ number, title, description, highlight }) => (
  <div
    className="flex items-start gap-3 p-3 rounded-lg border transition-colors duration-150"
    style={{
      borderColor: highlight ? 'var(--secondary-200)' : 'var(--neutral-200)',
      backgroundColor: highlight ? 'var(--secondary-50)' : 'var(--neutral-50)',
    }}
  >
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
      style={{
        backgroundColor: highlight ? 'var(--secondary-400)' : 'var(--primary-400)',
        color: 'white',
      }}
    >
      {number}
    </div>
    <div>
      <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
        {title}
      </h4>
      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {description}
      </p>
    </div>
  </div>
);

const ConsentItem = ({ children }) => (
  <li className="flex items-start gap-2">
    <CheckCircle className="h-4 w-4 mt-0.5 shrink-0 text-[var(--primary-400)]" />
    <span>{children}</span>
  </li>
);
